import { Injectable, inject } from "@angular/core";
import { AlertService } from "@ui";
import { TRANSFORM_TYPE, type Transform, type TransformWithCorpus } from "@pienso/ingest-types/transform.interface";
import {
  CorpusDataService,
  FindDeleteRowsTransformDetails,
  FindReplaceTransformDetails,
  type TransformDetails,
} from "../../services/corpus-data.service";
import { ElasticSearchService } from "../../services/elastic-search.service";
import { AbstractTransformer } from "../abstract-transformer";
import { TRANSFORMER_STATE } from "../abstract-transformer.service";
import { ToolbarItem } from "../toolbar-item";
import { TransformerGridDataService } from "../transformer-grid-data.service";
import { FIND_REPLACE_ACTIONS, FindReplaceService } from "./find-replace.service";
import { FindReplaceGridDataProvider } from "./find-replace-grid-data";
import { FindReplaceRendererProvider } from "./find-replace-renderer.provider";
import { FindReplaceToolbarComponent } from "./find-replace-toolbar.component";

@Injectable({ providedIn: "root" })
export class FindReplaceTransformerService extends AbstractTransformer {
  protected override dataService: TransformerGridDataService;
  protected override corpusService: CorpusDataService;
  protected override alertService: AlertService;
  private elastic: ElasticSearchService;
  override service: FindReplaceService;

  constructor() {
    const dataService = inject(TransformerGridDataService);
    const corpusService = inject(CorpusDataService);
    const alertService = inject(AlertService);
    const elastic = inject(ElasticSearchService);
    const service = inject(FindReplaceService);

    super(
      "findReplace",
      "Find & replace",
      [TRANSFORM_TYPE.FIND_REPLACE, TRANSFORM_TYPE.FIND_DELETE_ROWS],
      corpusService,
      alertService,
      service,
      dataService,
      new ToolbarItem(FindReplaceToolbarComponent),
      new FindReplaceGridDataProvider(dataService, elastic, service),
      new FindReplaceRendererProvider(service),
    );
    this.dataService = dataService;
    this.corpusService = corpusService;
    this.alertService = alertService;
    this.elastic = elastic;
    this.service = service;

    this._dataProvider = new FindReplaceGridDataProvider(dataService, elastic, service);
    service.findText$.subscribe((txt) => {
      if (service.state !== TRANSFORMER_STATE.EDITING) {
        service.state = TRANSFORMER_STATE.EDITING;
      }
    });
    service.replaceText$.subscribe((txt) => {
      if (service.state !== TRANSFORMER_STATE.EDITING) {
        service.state = TRANSFORMER_STATE.EDITING;
      }
    });
    service.action$.subscribe((action) => {
      switch (action) {
        case FIND_REPLACE_ACTIONS.REPLACE:
        case FIND_REPLACE_ACTIONS.REMOVE:
          this.commit(this.createReplaceSavePayload());
          break;
        case FIND_REPLACE_ACTIONS.DELETE_ROWS:
          this.commit(this.createDeleteRowsSavePayload());
          break;
      }
    });
  }

  public describe(xform: Transform): string {
    const d: any = xform.details;
    switch (xform.transformType as TRANSFORM_TYPE) {
      case TRANSFORM_TYPE.FIND_REPLACE:
        if (!d.replace || d.replace.length === 0) {
          return `${d.field}: Removed "${d.find}"`;
        }
        return `${d.field}: Replaced "${d.find}" with "${d.replace}"`;
      case TRANSFORM_TYPE.FIND_DELETE_ROWS:
        return `Deleted rows containing "${d.find}" in ${d.field}`;
      default:
        throw new Error("Incompatible transform type for Find-Replace transform: " + xform.transformType);
    }
  }

  public override get gridDataProvider() {
    (<FindReplaceGridDataProvider>this._dataProvider).corpus = this.corpus;
    return this._dataProvider;
  }

  protected createSavePayload(): TransformDetails {
    return this.createReplaceSavePayload();
  }

  private createReplaceSavePayload(): TransformDetails {
    if (!this.service.columnId) {
      throw new Error("Column ID is not set");
    }
    return new FindReplaceTransformDetails({
      field: this.service.columnId,
      find: this.service.findText,
      replace: this.service.replaceText,
    });
  }

  private createDeleteRowsSavePayload(): TransformDetails {
    if (!this.service.columnId) {
      throw new Error("Column ID is not set");
    }
    return new FindDeleteRowsTransformDetails({
      field: this.service.columnId,
      find: this.service.findText,
    });
  }

  protected initFromTransform(xform: Transform) {
    if (
      xform.transformType !== TRANSFORM_TYPE.FIND_REPLACE &&
      xform.transformType !== TRANSFORM_TYPE.FIND_DELETE_ROWS
    ) {
      throw new Error("Incompatible transform details for Find-Replace transformer: " + xform.transformType);
    }
    const d = <any>xform.details;
    this.service.columnId = d.field;
    this.service.findText = d.find;
    this.service.replaceText = xform.transformType === TRANSFORM_TYPE.FIND_REPLACE ? d.replace : "";
  }

  public override completed(xform: TransformWithCorpus) {
    super.completed(xform);
    (<FindReplaceGridDataProvider>this.gridDataProvider).corpus = xform.corpus;
  }
}
